/* eslint-disable no-nested-ternary */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatter, type RefinedTrace } from '@repo/shared';
import { GitCommitVertical } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useOpenTrace } from '@/routes/(app)/(tracing)/hooks/use-open-trace';

const TracesTableSkeleton = ({ colsCount }: { colsCount: number }) => (
  <TableBody>
    <TableRow>
      {Array.from({ length: colsCount }).map((_, index) => (
        <TableCell key={index}>
          <Skeleton className='w-1/2' />
        </TableCell>
      ))}
    </TableRow>
  </TableBody>
);

const TracesTableEmpty = ({ colsCount }: { colsCount: number }) => {
  const { t } = useTranslation();
  return (
    <TableBody>
      <TableRow>
        <TableCell
          colSpan={colsCount}
          className='px-2 py-1 text-center'>
          {t('tracing.tracesTable.noTraces')}
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

const TracesTableError = ({
  error,
  colsCount,
}: {
  error: { message: string };
  colsCount: number;
}) => (
  <TableBody>
    <TableRow>
      <TableCell
        colSpan={colsCount}
        className='px-2 py-1 text-center'>
        {error.message}
      </TableCell>
    </TableRow>
  </TableBody>
);

export interface TracesTableProps {
  traces: Array<RefinedTrace>;
  isLoading: boolean;
  error?: { message: string } | null;
}

const TraceRow = ({ trace, index }: { trace: RefinedTrace; index: number }) => {
  const { openTrace } = useOpenTrace();
  const { t } = useTranslation();
  const handleOpenTrace = useCallback(() => {
    openTrace(trace.trace, index);
  }, [index, openTrace, trace.trace]);

  return (
    <TableRow>
      <TableCell className='px-2 py-1'>
        {formatter.date.dateTime(new Date(trace.started / 1000))}
      </TableCell>
      <TableCell className='px-2 py-1'>{trace.traceId}</TableCell>
      <TableCell className='px-2 py-1'>{trace.duration} ms</TableCell>
      <TableCell className='px-2 py-1'>
        <Badge
          variant='default'
          onClick={handleOpenTrace}>
          <Badge.Icon side='left'>
            <GitCommitVertical />
          </Badge.Icon>
          {t('tracing.tracesTable.amountOfSpans', {
            count: trace.trace.length,
          })}
        </Badge>
      </TableCell>
    </TableRow>
  );
};

export const TracesTable = ({ traces, isLoading, error }: TracesTableProps) => {
  const hasNoTraces = traces.length === 0;
  const colsCount = 4;
  const { t } = useTranslation();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('tracing.tracesTable.time')}</TableHead>
          <TableHead>{t('tracing.tracesTable.traceId')}</TableHead>
          <TableHead>{t('tracing.tracesTable.duration')}</TableHead>
          <TableHead>{t('tracing.tracesTable.spans')}</TableHead>
        </TableRow>
      </TableHeader>
      {isLoading ? (
        <TracesTableSkeleton colsCount={colsCount} />
      ) : error ? (
        <TracesTableError
          error={error}
          colsCount={colsCount}
        />
      ) : hasNoTraces ? (
        <TracesTableEmpty colsCount={colsCount} />
      ) : (
        <TableBody>
          {traces.map((trace, index) => (
            <TraceRow
              key={trace.traceId}
              trace={trace}
              index={index}
            />
          ))}
        </TableBody>
      )}
    </Table>
  );
};
